/* Programme Pulse — one screen, every programme's operating heartbeat. */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Activity, BarChart3 } from 'lucide-react';

const COLS = [
  ['members', 'Members'], ['souls_filled', 'Souls filled'],
  ['requests_raised', 'Requests raised'], ['tickets_closed', 'Closed'],
  ['greetings_sent', 'Greetings sent'], ['facts_added', 'Soul facts added'],
];

export default function ProgrammePulsePage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/programme-pulse?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  return (
    <div className="p-8 max-w-6xl space-y-5" data-testid="programme-pulse-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: '#1B2A4A', fontFamily: 'Fraunces, serif' }}>
            <Activity size={26} color="#B88C40" /> Programme Pulse
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Every programme's operating heartbeat — activity in the selected window.
          </p>
        </div>
        <div className="flex gap-2">
          {[30, 90, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              data-testid={`pulse-window-${d}`}
              className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border ${
                days === d ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'bg-white text-[#1B2A4A] border-[#d8d4cc]'
              }`}>{d}d</button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-[#9CA3AF]">Loading…</div>
      ) : (
        <div className="rounded-xl border border-[#e5e2dc] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="programme-pulse-table">
            <thead className="bg-[#faf6f0] border-b border-[#e5e2dc]">
              <tr>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#9CA3AF]">Programme</th>
                {COLS.map(([k, h]) => (
                  <th key={k} className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#9CA3AF]">{h}</th>
                ))}
                <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#9CA3AF]">Client MIS</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.client_id} className="border-b border-[#f0ede7] last:border-b-0 hover:bg-[#fbf9f5]"
                    data-testid={`pulse-row-${r.client_id}`}>
                  <td className="px-4 py-2.5 font-semibold text-[#1B2A4A]">{r.label}</td>
                  {COLS.map(([k]) => (
                    <td key={k} className={`px-4 py-2.5 text-right tabular-nums ${r[k] ? 'text-[#1B2A4A]' : 'text-[#C9C1AE]'}`}>
                      {r[k].toLocaleString()}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right">
                    <Link to={`/client-mis/${r.client_id}`} data-testid={`pulse-mis-link-${r.client_id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B88C40] hover:underline">
                      <BarChart3 size={12} /> MIS
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[12px] text-[#9CA3AF]">
        Members & souls are all-time; requests, closures, greetings and facts are within the window.
        Financial five-lines stay in Group MIS — this is the operations view.
      </p>
    </div>
  );
}
